// src/components/NewTranslation.js

import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Container,
    Button,
    Typography,
    Paper,
    Box,
    TextField,
    Select,
    FormControl,
    FormControlLabel,
    Grid,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Checkbox,
    ListItemText,
    Snackbar,
    Alert,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Breadcrumbs,
    Switch,
    Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Flag from 'react-world-flags';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import GoogleIcon from '@mui/icons-material/Google';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FileIcon, defaultStyles } from 'react-file-icon';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import isoLanguages from './isoLanguages';
import { stringToPastelColor, stringToDarkColor } from '../utils/colorUtils';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

const getLanguageDetails = (code) =>
    isoLanguages.find((lang) => lang.code.toLowerCase() === code?.toLowerCase()) || {
        name: code,
        countryCode: 'UN',
    };

// Styled components
const DropzoneBox = styled(Box)(({ theme, isdrag }) => ({
    border: '2px dashed gray',
    borderRadius: '15px',
    backgroundColor: isdrag ? '#f0f0f0' : '#fafafa',
    padding: '50px',
    textAlign: 'center',
    cursor: 'pointer',
    marginBottom: '20px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'background-color 0.2s ease-in-out',
}));

const SelectedChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.5),
}));

export default function NewTranslation() {
    const [file, setFile] = useState(null);
    const [fileNameOnServer, setFileNameOnServer] = useState('');
    const [error, setError] = useState('');
    const [projectName, setProjectName] = useState('');
    const [sourceLanguage, setSourceLanguage] = useState('');
    const [targetLanguages, setTargetLanguages] = useState([]);
    const [deadline, setDeadline] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState('');
    const [columnOptions, setColumnOptions] = useState(null);
    const [worksheetNames, setWorksheetNames] = useState(null);
    const [selectedSheet, setSelectedSheet] = useState('');
    const [sourceColumn, setSourceColumn] = useState('');
    const [targetColumns, setTargetColumns] = useState({});
    const [mappingOpen, setMappingOpen] = useState(false);
    const [mappingDismissed, setMappingDismissed] = useState(false);
    const [targetSelectOpen, setTargetSelectOpen] = useState(false);
    const [driveLink, setDriveLink] = useState('');
    const [importingLink, setImportingLink] = useState(false);
    const [driveModalOpen, setDriveModalOpen] = useState(false);
    const [driveFiles, setDriveFiles] = useState([]);
    const [driveLoading, setDriveLoading] = useState(false);
    const [driveParent, setDriveParent] = useState('root');
    const [driveBreadcrumbs, setDriveBreadcrumbs] = useState([{ id: 'root', name: 'My Drive' }]);
    const [useOcr, setUseOcr] = useState(false);
    const [tags, setTags] = useState([]);

    const { token, user } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const location = useLocation();

    const deleteFile = useCallback(
        async (manual = false) => {
            if (!fileNameOnServer) return;
            try {
                await axios.delete(
                    `/api/new-translation/delete-file/${encodeURIComponent(fileNameOnServer)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (err) {
                console.error('Error deleting file:', err);
            } finally {
                setFile(null);
                setFileNameOnServer('');
                setUploadProgress(0);
                setColumnOptions(null);
                if (manual) setError('');
            }
        },
        [fileNameOnServer, token]
    );

    const handleImportFromLink = async () => {
        if (!driveLink.trim()) return;
        setImportingLink(true);
        setError('');
        try {
            const { data: newFile } = await axios.post('/api/integrations/google/import-link', {
                url: driveLink,
                isTemporary: true
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Fetch the actual file blob to satisfy the existing project creation flow which uses a File object
            const res = await axios.get(`/api/files/download/${newFile._id}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const fetchedFile = new File([res.data], newFile.name);
            setFile(fetchedFile);
            setProjectName(newFile.name.replace(/\.[^/.]+$/, ''));
            preUploadFile(fetchedFile);
            setDriveLink('');
        } catch (err) {
            console.error('Failed to import from link:', err);
            setError(err.response?.data?.msg || 'Failed to import from Google Drive link. Make sure you are connected to Google Drive.');
        } finally {
            setImportingLink(false);
        }
    };

    const fetchDriveFiles = useCallback(async (folderId = 'root') => {
        setDriveLoading(true);
        try {
            const { data } = await axios.get("/api/integrations/google/list", {
                headers: { Authorization: `Bearer ${token}` },
                params: { folderId }
            });
            setDriveFiles(data);
        } catch (err) {
            console.error("Failed to fetch Google Drive files", err);
        } finally {
            setDriveLoading(false);
        }
    }, [token]);

    const handleDriveFolderClick = (folder) => {
        setDriveBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
        setDriveParent(folder.id);
        fetchDriveFiles(folder.id);
    };

    const handleDriveBreadcrumbClick = (crumb, index) => {
        const newCrumbs = driveBreadcrumbs.slice(0, index + 1);
        setDriveBreadcrumbs(newCrumbs);
        setDriveParent(crumb.id);
        fetchDriveFiles(crumb.id);
    };

    const handleDriveImport = async (df) => {
        setDriveModalOpen(false);
        setImportingLink(true);
        setError('');
        try {
            const { data: newFile } = await axios.post('/api/integrations/google/import', {
                fileId: df.id,
                isTemporary: true
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const res = await axios.get(`/api/files/download/${newFile._id}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const fetchedFile = new File([res.data], newFile.name);
            setFile(fetchedFile);
            setProjectName(newFile.name.replace(/\.[^/.]+$/, ''));
            preUploadFile(fetchedFile);
        } catch (err) {
            console.error('Failed to import from Drive:', err);
            setError('Failed to import from Google Drive.');
        } finally {
            setImportingLink(false);
        }
    };

    // Pre‐upload the file when selected/dropped
    const preUploadFile = useCallback(
        async (selectedFile) => {
            if (!selectedFile) {
                setError('Please select or drop a file.');
                return;
            }
            const formData = new FormData();
            formData.append('file', selectedFile);

            try {
                const response = await axios.post(
                    '/api/new-translation/pre-upload',
                    formData,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        onUploadProgress: (progressEvent) => {
                            const progress = Math.round(
                                (progressEvent.loaded * 100) / progressEvent.total
                            );
                            setUploadProgress(progress);
                        },
                    }
                );

                setFileNameOnServer(response.data.fileName);
                if (response.data.worksheetNames) {
                    setWorksheetNames(response.data.worksheetNames);
                    setSelectedSheet(response.data.worksheetNames[0] || '');
                }
                if (response.data.headers) {
                    setColumnOptions(response.data.headers);
                    setSourceColumn('');
                    setTargetColumns({});
                    setMappingDismissed(false);
                } else {
                    setColumnOptions(null);
                }
            } catch (err) {
                console.error('Error pre-uploading file:', err);
                setError('Failed to upload file.');
            }
        },
        [token]
    );

    // Handle file drop
    const onDrop = useCallback(
        (acceptedFiles) => {
            const selected = acceptedFiles[0];
            if (!selected) return;

            // Client‐side validation: file type & size
            const allowedExt = [
                '.doc',
                '.docx',
                '.idml',
                '.html',
                '.pptx',
                '.json',
                '.xml',
                '.xlf',
                '.xliff',
                '.xlsx',
                '.csv',
                '.po',
                '.srt',
                '.pdf',
            ];
            const ext = '.' + selected.name.split('.').pop().toLowerCase();
            if (!allowedExt.includes(ext)) {
                setError(
                    'Unsupported file type. Allowed: .doc, .docx, .idml, .html, .pptx, .json, .xml, .xlf, .xliff, .xlsx, .csv, .po, .srt, .pdf'
                );
                return;
            }
            if (selected.size > MAX_FILE_SIZE) {
                setError(
                    `File too large. Max size is ${(MAX_FILE_SIZE / (1024 * 1024)).toFixed(
                        1
                    )} MB.`
                );
                return;
            }

            // Confirm overwrite if a file is already present
            if (file && fileNameOnServer) {
                const confirmReplace = window.confirm(
                    `You already uploaded "${file.name}". Replacing it will delete the existing upload. Continue?`
                );
                if (!confirmReplace) return;
                deleteFile();
            }

            setFile(selected);
            setError('');
            setUploadProgress(0);
            setWorksheetNames(null);
            setSelectedSheet('');
            setSourceColumn('');
            setTargetColumns({});
            setMappingOpen(false);
            setMappingDismissed(false);

            preUploadFile(selected);
        },
        [file, fileNameOnServer, preUploadFile, deleteFile]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/msword': ['.doc', '.docx'],
            'application/vnd.idml+xml': ['.idml'],
            'text/html': ['.html'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
                '.pptx',
            ],
            'application/json': ['.json'],
            'application/xml': ['.xml', '.xlf', '.xliff'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv'],
            'text/x-gettext-translation': ['.po'],
            'text/plain': ['.srt'],
            'application/pdf': ['.pdf'],
        },
        multiple: false,
    });

    // If navigated from File Manager with a fileId, load that file
    useEffect(() => {
        const fileId = location.state && location.state.fileId;
        if (!fileId || file) return;
        const fetchFile = async () => {
            try {
                const { data: meta } = await axios.get(`/api/files/${fileId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const res = await axios.get(`/api/files/download/${fileId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                });
                const fetchedFile = new File([res.data], meta.name);
                setFile(fetchedFile);
                setProjectName(meta.name.replace(/\.[^/.]+$/, ''));
                preUploadFile(fetchedFile);
            } catch (err) {
                console.error('Failed to preload file:', err);
            }
        };
        fetchFile();
    }, [location.state, file, preUploadFile, token]);

    const handleRemoveTarget = (code) => {
        setTargetLanguages((prev) => prev.filter((c) => c !== code));
        setTargetColumns((prev) => {
            const updated = { ...prev };
            delete updated[code];
            return updated;
        });
        setMappingDismissed(false);
    };

    const fetchHeaders = useCallback(
        async (sheet) => {
            if (!fileNameOnServer) return;
            try {
                const res = await axios.get('/api/new-translation/xlsx-headers', {
                    params: { fileName: fileNameOnServer, sheetName: sheet },
                    headers: { Authorization: `Bearer ${token}` },
                });
                setColumnOptions(res.data.headers);
                setSourceColumn('');
                setTargetColumns({});
            } catch (err) {
                console.error('Failed to fetch headers:', err);
            }
        },
        [fileNameOnServer, token]
    );

    const handleSheetChange = async (e) => {
        const sheet = e.target.value;
        setSelectedSheet(sheet);
        await fetchHeaders(sheet);
        setMappingDismissed(false);
    };

    // Only open mapping dialog once languages are selected and dropdown is closed
    useEffect(() => {
        if (
            !mappingOpen &&
            !mappingDismissed &&
            !targetSelectOpen &&
            columnOptions &&
            sourceLanguage &&
            targetLanguages.length > 0
        ) {
            setMappingOpen(true);
        }
    }, [mappingOpen, mappingDismissed, targetSelectOpen, columnOptions, sourceLanguage, targetLanguages]);

    const handleProjectCreation = async () => {
        setError('');

        // Basic validation
        if (!file || !projectName.trim() || !sourceLanguage || targetLanguages.length === 0) {
            setError(
                'Project name, source language, at least one target language, and file are required.'
            );
            return;
        }
        setSubmitting(true);

        try {
            const adjustedTargets = targetLanguages.map((lang) =>
                lang.toLowerCase() === 'en' ? 'en-gb' : lang.toLowerCase()
            );

            const formData = new FormData();
            formData.append('file', file);
            formData.append('projectName', projectName);
            formData.append('sourceLanguage', sourceLanguage.toLowerCase());
            formData.append('targetLanguages', JSON.stringify(adjustedTargets));
            formData.append('deadline', deadline);
            formData.append('useOcr', useOcr);
            if (selectedSheet) formData.append('sheetName', selectedSheet);
            if (sourceColumn) formData.append('sourceColumn', sourceColumn);
            formData.append('targetColumns', JSON.stringify(targetColumns));
            formData.append('tags', JSON.stringify(tags));

            await axios.post('/api/new-translation/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(progress);
                },
            });

            // Show success via Snackbar
            setSnackbarMsg('Project created successfully! Redirecting…');
            setOpenSnackbar(true);

            // Delay then redirect to /dashboard/projects
            setTimeout(() => {
                setOpenSnackbar(false);
                navigate('/dashboard/projects');
            }, 1500);

            // Reset fields
            setFile(null);
            setFileNameOnServer('');
            setProjectName('');
            setSourceLanguage('');
            setTargetLanguages([]);
            setDeadline('');
            setUploadProgress(0);
            setColumnOptions(null);
            setWorksheetNames(null);
            setSelectedSheet('');
            setSourceColumn('');
            setTargetColumns({});
            setTags([]);
        } catch (err) {
            console.error('Error uploading file:', err);
            setError('Error uploading or processing the file.');
        } finally {
            setSubmitting(false);
        }
    };

    const formatFileSize = (size) => {
        if (size < 1024 * 1024) {
            return `${(size / 1024).toFixed(2)} KB`;
        } else {
            return `${(size / (1024 * 1024)).toFixed(2)} MB`;
        }
    };

    const getFileExtension = (name = '') => {
        const parts = name.split('.');
        if (parts.length < 2) return '';
        return parts.pop().toLowerCase();
    };

    const renderFileIcon = (name, mimeType) => {
        let ext = getFileExtension(name);

        // Map Google MimeTypes to familiar icons
        if (mimeType === 'application/vnd.google-apps.document') ext = 'docx';
        if (mimeType === 'application/vnd.google-apps.spreadsheet') ext = 'xlsx';
        if (mimeType === 'application/vnd.google-apps.presentation') ext = 'pptx';

        if (defaultStyles[ext]) {
            return (
                <Box sx={{ width: 32 }}>
                    <FileIcon extension={ext} {...defaultStyles[ext]} />
                </Box>
            );
        }
        return (
            <Box sx={{ width: 32 }}>
                <FileIcon extension={ext || 'file'} />
            </Box>
        );
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (fileNameOnServer && location.pathname !== '/new-translation/delete-file') {
                deleteFile();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (fileNameOnServer && location.pathname !== '/new-translation/delete-file') {
                deleteFile();
            }
        };
    }, [deleteFile, fileNameOnServer, location.pathname]);

    // Disable button until mandatory fields are filled and not submitting
    const mappingValid = !columnOptions ||
        (sourceColumn && targetLanguages.every((lang) => targetColumns[lang]));

    const canSubmit =
        file &&
        projectName.trim() !== '' &&
        sourceLanguage &&
        targetLanguages.length > 0 &&
        !submitting &&
        mappingValid;

    return (
        <Container maxWidth="md" sx={{ marginTop: '50px' }}>
            <Paper elevation={3} sx={{ padding: '30px', borderRadius: '10px' }}>
                <Typography variant="h4" gutterBottom>
                    Create a New Translation Project
                </Typography>

                {/* 1. Project Name */}
                <Box sx={{ marginBottom: '20px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Project Name&nbsp;
                        <Tooltip title="Give a descriptive name, e.g., ‘Employee Handbook EN→FR’">
                            <InfoIcon fontSize="small" />
                        </Tooltip>
                    </Typography>
                    <TextField
                        variant="outlined"
                        fullWidth
                        required
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        helperText="Make it descriptive (e.g. ‘Employee Handbook’)."
                    />
                </Box>

                {/* Tags */}
                <Box sx={{ marginBottom: '20px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Tags (Optional)&nbsp;
                        <Tooltip title="Add tags to group and filter your translations later (e.g. 'Marketing', 'Q3', 'Project Alpha')">
                            <InfoIcon fontSize="small" />
                        </Tooltip>
                    </Typography>
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={tags}
                        onChange={(event, newValue) => setTags(newValue)}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option}
                                    {...getTagProps({ index })}
                                    key={index}
                                    size="small"
                                    sx={{
                                        bgcolor: stringToPastelColor(option),
                                        color: stringToDarkColor(option),
                                        fontWeight: 600,
                                        border: `1px solid ${stringToDarkColor(option)}`,
                                    }}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Type a tag and press Enter"
                                helperText="Press Enter to add a tag."
                            />
                        )}
                    />
                </Box>

                {/* 2. Source & Target Languages */}
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item xs={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Source Language&nbsp;
                            <Tooltip title="Choose the file’s original language.">
                                <InfoIcon fontSize="small" />
                            </Tooltip>
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                value={sourceLanguage}
                                onChange={(e) => { setSourceLanguage(e.target.value); setMappingDismissed(false); }}
                                displayEmpty
                                required
                            >
                                <MenuItem value="">
                                    <em>Select source</em>
                                </MenuItem>
                                {isoLanguages
                                    .filter(lang => {
                                        const accountType = user?.accountType || 'free';
                                        if (accountType === 'free') return lang.tier <= 1;
                                        if (accountType === 'essential') return lang.tier <= 2;
                                        return true; // growth/scale or other high tiers
                                    })
                                    .map((language) => (
                                        <MenuItem key={language.code} value={language.code}>
                                            <Flag
                                                code={language.countryCode}
                                                style={{ marginRight: '10px', width: '20px' }}
                                            />
                                            {language.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Target Languages&nbsp;
                            <Tooltip title="Select one or more languages to translate into.">
                                <InfoIcon fontSize="small" />
                            </Tooltip>
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                value={targetLanguages}
                                onChange={(e) => { setTargetLanguages(e.target.value); setMappingDismissed(false); }}
                                onOpen={() => setTargetSelectOpen(true)}
                                onClose={() => setTargetSelectOpen(false)}
                                multiple
                                displayEmpty
                                required
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <em>Select targets</em>;
                                    }
                                    return selected.map((code) => getLanguageDetails(code).name).join(', ');
                                }}
                            >
                                {isoLanguages
                                    .filter(lang => {
                                        const accountType = user?.accountType || 'free';
                                        if (accountType === 'free') return lang.tier <= 1;
                                        if (accountType === 'essential') return lang.tier <= 2;
                                        return true;
                                    })
                                    .map((language) => (
                                        <MenuItem key={language.code} value={language.code}>
                                            <Checkbox checked={targetLanguages.includes(language.code)} />
                                            <ListItemText
                                                primary={
                                                    <>
                                                        <Flag
                                                            code={language.countryCode}
                                                            style={{ marginRight: '10px', width: '20px' }}
                                                        />
                                                        {language.name}
                                                    </>
                                                }
                                            />
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap' }}>
                            {targetLanguages.map((code) => {
                                const lang = getLanguageDetails(code);
                                return (
                                    <SelectedChip
                                        key={code}
                                        icon={<Flag code={lang.countryCode} style={{ width: '20px' }} />}
                                        label={lang.name}
                                        onDelete={() => handleRemoveTarget(code)}
                                        size="small"
                                    />
                                );
                            })}
                        </Box>
                    </Grid>
                </Grid>

                {/* 4. Deadline */}
                <Box sx={{ marginBottom: '20px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Deadline&nbsp;
                        <Tooltip title="Choose a due date. Past dates are disabled.">
                            <InfoIcon fontSize="small" />
                        </Tooltip>
                    </Typography>
                    <TextField
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={deadline}
                        onChange={(e) => setDeadline(e.target.value)}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                        helperText="Select a deadline (today or later)."
                    />
                </Box>

                {/* 4. File dropzone */}
                <DropzoneBox {...getRootProps()} isdrag={isDragActive ? 1 : 0}>
                    <input {...getInputProps()} />
                    {isDragActive ? (
                        <Typography>Drop the file here...</Typography>
                    ) : (
                        <Typography>
                            {file
                                ? `Selected File: ${file.name}`
                                : 'Drag & drop a .docx, .pptx, .html, .json, .xml, .xlf/.xliff, .idml, .xlsx, .csv, .po, .srt, or .pdf file here, or click to select'}
                        </Typography>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                        Max file size: {(MAX_FILE_SIZE / (1024 * 1024)).toFixed(1)} MB
                    </Typography>
                </DropzoneBox>

                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        — OR —
                    </Typography>

                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Paste Google Drive Link"
                                value={driveLink}
                                onChange={(e) => setDriveLink(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LinkIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleImportFromLink}
                                disabled={!driveLink.trim() || importingLink}
                                startIcon={importingLink ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
                            >
                                Import Link
                            </Button>
                        </Grid>
                    </Grid>

                    <Button
                        sx={{ mt: 2 }}
                        startIcon={<GoogleIcon />}
                        onClick={() => {
                            setDriveModalOpen(true);
                            fetchDriveFiles('root');
                        }}
                    >
                        Browse Google Drive
                    </Button>
                </Box>

                {/* 5. File details & Clear button */}
                {file && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px',
                        }}
                    >
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {renderFileIcon(file.name)}
                                <Typography variant="body1">{file.name}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                File Size: {formatFileSize(file.size)}
                            </Typography>
                            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                        </Box>
                        <IconButton color="error" onClick={() => deleteFile(true)} size="small" sx={{ ml: 2 }}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                )}

                {/* OCR Toggle — only visible for PDF files */}
                {file && file.name.toLowerCase().endsWith('.pdf') && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={useOcr}
                                    onChange={(e) => setUseOcr(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Enable OCR (for scanned PDFs)"
                        />
                        <Tooltip title="Enable this if your PDF contains scanned images of text rather than selectable text. Uses Adobe OCR to extract content.">
                            <InfoIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary' }} />
                        </Tooltip>
                    </Box>
                )}

                {columnOptions && !mappingOpen && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setMappingDismissed(false); setMappingOpen(true); }}
                        sx={{ mb: 2 }}
                    >
                        Edit Column Mapping
                    </Button>
                )}

                {/* Mapping dialog rendered below */}

                {/* 6. Create Project button + spinner */}
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleProjectCreation}
                    fullWidth
                    sx={{ marginTop: '20px', padding: '10px 0', fontSize: '16px' }}
                    disabled={!canSubmit}
                >
                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Create Project'}
                </Button>

                {error && (
                    <Typography color="error" sx={{ marginTop: '20px' }}>
                        {error}
                    </Typography>
                )}

                <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Map Columns</DialogTitle>
                    <DialogContent>
                        {worksheetNames && (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Worksheet
                                </Typography>
                                <Select value={selectedSheet} onChange={handleSheetChange}>
                                    {worksheetNames.map((name) => (
                                        <MenuItem key={name} value={name}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {columnOptions && (
                            <>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Source Column
                                </Typography>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <Select value={sourceColumn} onChange={(e) => setSourceColumn(e.target.value)} displayEmpty>
                                        <MenuItem value="">
                                            <em>Select column</em>
                                        </MenuItem>
                                        {columnOptions.map((h, idx) => (
                                            <MenuItem key={idx} value={h}>{h}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {targetLanguages.map((lang) => (
                                    <FormControl fullWidth sx={{ mb: 2 }} key={lang}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                            {getLanguageDetails(lang).name}
                                        </Typography>
                                        <Select
                                            value={targetColumns[lang] || ''}
                                            onChange={(e) =>
                                                setTargetColumns((prev) => ({ ...prev, [lang]: e.target.value }))
                                            }
                                            displayEmpty
                                        >
                                            <MenuItem value="">
                                                <em>Select column</em>
                                            </MenuItem>
                                            {columnOptions.map((h, idx) => (
                                                <MenuItem key={idx} value={h}>{h}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ))}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { setMappingOpen(false); setMappingDismissed(true); }}>
                            Cancel
                        </Button>
                        <Button onClick={() => { setMappingOpen(false); setMappingDismissed(true); }}>
                            Manual Edit
                        </Button>
                        <Button onClick={() => { setMappingOpen(false); setMappingDismissed(true); }} disabled={!mappingValid}>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for success */}
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
                        {snackbarMsg}
                    </Alert>
                </Snackbar>

                {/* Google Drive Picker Modal */}
                <Dialog open={driveModalOpen} onClose={() => setDriveModalOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GoogleIcon color="primary" />
                        Browse Google Drive
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box sx={{ mb: 2 }}>
                            <Breadcrumbs>
                                {driveBreadcrumbs.map((crumb, idx) => (
                                    <Button
                                        key={crumb.id}
                                        size="small"
                                        onClick={() => handleDriveBreadcrumbClick(crumb, idx)}
                                        disabled={idx === driveBreadcrumbs.length - 1}
                                    >
                                        {crumb.name}
                                    </Button>
                                ))}
                            </Breadcrumbs>
                        </Box>

                        {driveLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {driveFiles.length === 0 ? (
                                    <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                        No files found or not connected.
                                    </Typography>
                                ) : (
                                    driveFiles.map((df) => (
                                        <Box
                                            key={df.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 1,
                                                borderBottom: '1px solid #eee',
                                                '&:hover': { bgcolor: '#f5f5f5', cursor: 'pointer' }
                                            }}
                                            onClick={() => df.mimeType === 'application/vnd.google-apps.folder' ? handleDriveFolderClick(df) : null}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {df.mimeType === 'application/vnd.google-apps.folder' ? <FolderIcon color="warning" /> : renderFileIcon(df.name, df.mimeType)}
                                                <Typography variant="body2">{df.name}</Typography>
                                            </Box>
                                            {df.mimeType !== 'application/vnd.google-apps.folder' && (
                                                <Button size="small" onClick={(e) => { e.stopPropagation(); handleDriveImport(df); }}>
                                                    Import
                                                </Button>
                                            )}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDriveModalOpen(false)}>Cancel</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
}
